import { useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';

import { ObjectThumbnail } from '../../editor/thumbnails/ObjectThumbnail';
import { useThumbnailStore } from '../../editor/thumbnails/thumbnailStore';
import { libraryAuthorUrl, LIBRARY_ID_PREFIX } from '../../content/packs/library/libraryPack';
import {
  getLibraryLoadError,
  getLibraryLoadStatus,
  retryLibraryLoad,
  subscribeLibraryLoadStatus,
} from '../../content/packs/library/libraryLoader';
import {
  getObjectRegistryVersion,
  listObjectDefinitions,
  SOULSTACK_ORIGINALS_AUTHOR,
  subscribeObjectRegistry,
  type ObjectCategory,
  type ObjectDefinition,
} from '../../content/registry/objectRegistry';
import { useDebouncedValue } from './useDebouncedValue';

/** Search input is debounced by this much before it drives the ~5k-item
 * library scan - typing "kenney" no longer re-runs matchesSearch 5,100
 * times per keystroke, only once the user pauses. */
const SEARCH_DEBOUNCE_MS = 150;

const CATEGORY_ORDER: ObjectCategory[] = [
  'furniture',
  'industrial',
  'fixture',
  'effects',
  'gameplay',
  'structural',
];

const CATEGORY_LABELS: Record<ObjectCategory, string> = {
  furniture: 'Furniture',
  industrial: 'Industrial',
  fixture: 'Fixtures & Lights',
  effects: 'Effects & Decals',
  gameplay: 'Gameplay & Triggers',
  structural: 'Structural',
};

/** Tiles rendered per expanded bundle page and per search pass; Show more
 * reveals the next page. Keeps the DOM bounded with 5k+ library entries. */
const PAGE_SIZE = 96;
/** Cap on flat search results across the library. */
const SEARCH_CAP = 144;

interface ObjectBrowserProps {
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  selectedAssetId: string | null;
  onSelectAsset: (assetId: string) => void;
  title?: string;
  subtitle?: string;
  /** Extra class(es) for the search `<input>`, e.g. `"ss-input ss-input--compact"`
   * so a dense host (BottomDock's Library tab) can opt into the app's shared
   * compact input styling instead of this component's own default look.
   * Left unset, older hosts (MapEditorOverlay's sidebar) keep that default -
   * see the `.editor-search input:not(.ss-input)` guard in editor-theme.css. */
  searchInputClassName?: string;
  /** Skip this component's own eyebrow/title header and search field - the
   * host is rendering an equivalent search control itself (BottomDock's
   * Library tab, whose header search box already drives `searchQuery`/
   * `onSearchQueryChange` via a portal) and doesn't want it duplicated. */
  hideHeader?: boolean;
  /** Replace the "local" (non-library-prefixed) definitions grouped by
   * category with a single pinned "Soulstack Originals" author entry
   * (definitions whose `editor.author` is exactly that - see Core pack's
   * registrations) shown first, always expanded, ahead of the real Shared
   * library authors below it. Without this, opening Library would re-list
   * BottomDock's own Core pack/Uploads categories a second time (grouped as
   * Furniture/Structural/etc., with none of Uploads' rename/delete actions
   * and no attribution) instead of presenting Core pack as what it is: the
   * platform's own first-party content, same as it's branded elsewhere
   * (Showcase's "Soulstack Originals" dev profile). Uploads are never
   * pulled in here - they have no `editor.author` set. MapEditorOverlay's
   * legacy sidebar - the other host - has no separate Core-pack/Uploads
   * panel of its own, so this stays false there; the old category grouping
   * is its only way to browse either. */
  pinOriginals?: boolean;
}

/** Author/bundle for display: prefers the definition's own structured
 * `editor.author`/`editor.bundle` fields, falling back to parsing
 * `description` only for legacy entries still using the old
 * "Author · Bundle (licence)" convention (the shared library pack writes
 * both today; only structured fields matter for grouping/search going
 * forward). Never falls back on a description with no ' · ' separator - a
 * plain-sentence description (Core pack's own, before it had structured
 * fields) must not be mistaken for an attribution line. */
export function authorBundleOf(definition: ObjectDefinition): { author?: string; bundle?: string } {
  if (definition.editor.author || definition.editor.bundle) {
    return { author: definition.editor.author, bundle: definition.editor.bundle };
  }
  if (!definition.description.includes(' · ')) return {};
  const [author, bundlePart] = definition.description.split(' · ');
  return { author, bundle: bundlePart?.replace(/\s*\(.*\)$/, '') };
}

/** Card width including padding + border (editor.css: width 204px + 12px
 * padding each side + 1px border each side). Used to keep the card on-screen. */
export const HOVER_CARD_WIDTH = 230;
/** Rough card height for the vertical clamp - the card's real height varies
 * with tag count, but this covers the common case without measuring. */
export const HOVER_CARD_HEIGHT_ESTIMATE = 290;

/** Where the hover card should sit relative to a hovered tile's bounding
 * rect: to the right by default, flipped to the left when the tile is close
 * enough to the viewport's right edge (e.g. the dock's rightmost column)
 * that the card would otherwise render off-screen. Pure so it's testable
 * without mounting the tooltip. */
export function computeHoverCardPosition(
  rect: { top: number; left: number; right: number },
  viewportWidth: number,
  viewportHeight: number,
): { top: number; left: number; flipped: boolean } {
  const top = Math.max(0, Math.min(rect.top, viewportHeight - HOVER_CARD_HEIGHT_ESTIMATE));
  const fitsRight = rect.right + 10 + HOVER_CARD_WIDTH <= viewportWidth;
  const left = fitsRight ? rect.right + 10 : rect.left - 10 - HOVER_CARD_WIDTH;
  return { top, left, flipped: !fitsRight };
}

/** Tooltip-style large preview that floats beside a hovered tile - see
 * computeHoverCardPosition for the left/right flip logic. */
function HoverPreviewCard({ definition, rect }: {
  definition: ObjectDefinition;
  rect: DOMRect;
}) {
  const { author, bundle } = authorBundleOf(definition);
  const authorUrl = author ? libraryAuthorUrl(author) : null;
  const tags = (definition.editor.tags ?? [])
    .filter((t) => t !== 'library' && t !== 'animated')
    .slice(0, 5);
  const isAnimated = (definition.editor.tags ?? []).includes('animated');

  const { top, left, flipped } = computeHoverCardPosition(rect, window.innerWidth, window.innerHeight);

  return createPortal(
    <div
      className={`editor-library-hover-card${flipped ? ' is-flipped' : ''}`}
      style={{ top, left }}
    >
      <ObjectThumbnail assetId={definition.id} label={definition.name} size={176} />
      <div className="editor-library-hover-meta">
        <span className="editor-library-hover-name">
          {definition.name}
          {isAnimated && <span className="editor-library-anim-badge">ANIM</span>}
        </span>
        {(author || bundle) && (
          <span className="editor-library-hover-sub">
            {author}{bundle ? ` · ${bundle}` : ''}
            {authorUrl && ` - ${authorUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}`}
          </span>
        )}
        {tags.length > 0 && (
          <div className="editor-library-hover-tags">
            {tags.map((tag) => (
              <span key={tag} className="editor-library-hover-tag">{tag}</span>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export function matchesSearch(definition: ObjectDefinition, query: string) {
  if (!query.trim()) {
    return true;
  }

  const normalized = query.trim().toLowerCase();
  const haystack = [
    definition.name,
    definition.id,
    definition.description,
    definition.editor.author ?? '',
    definition.editor.bundle ?? '',
    ...(definition.editor.tags ?? []),
  ].join(' ').toLowerCase();

  return haystack.includes(normalized);
}

function ObjectTile({ definition, selected, onSelectAsset }: {
  definition: ObjectDefinition;
  selected: boolean;
  onSelectAsset: (assetId: string) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);

  return (
    <>
      <button
        ref={ref}
        type="button"
        draggable
        className={`editor-library-item${selected ? ' is-selected' : ''}`}
        onClick={() => onSelectAsset(definition.id)}
        onDragStart={(event) => {
          event.dataTransfer.setData('text/plain', definition.id);
          event.dataTransfer.effectAllowed = 'copy';
          // Arm placement so the ghost preview follows while dragging.
          onSelectAsset(definition.id);
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <ObjectThumbnail assetId={definition.id} label={definition.name} />
        <span className="editor-library-item-name">{definition.name}</span>
      </button>
      {hovered && ref.current && (
        <HoverPreviewCard definition={definition} rect={ref.current.getBoundingClientRect()} />
      )}
    </>
  );
}

/** A capped tile grid with a Show more pager. */
function TileGrid({ definitions, selectedAssetId, onSelectAsset }: {
  definitions: ObjectDefinition[];
  selectedAssetId: string | null;
  onSelectAsset: (assetId: string) => void;
}) {
  const [pages, setPages] = useState(1);
  const shown = definitions.slice(0, pages * PAGE_SIZE);
  return (
    <>
      <div className="editor-library-grid">
        {shown.map((definition) => (
          <ObjectTile
            key={definition.id}
            definition={definition}
            selected={selectedAssetId === definition.id}
            onSelectAsset={onSelectAsset}
          />
        ))}
      </div>
      {shown.length < definitions.length && (
        <button type="button" className="editor-library-more" onClick={() => setPages((p) => p + 1)}>
          Show more ({definitions.length - shown.length} remaining)
        </button>
      )}
    </>
  );
}

/** One library bundle: a collapsed header until opened, unless `forceOpen` -
 * used for the pinned Soulstack Originals entry, which should never need a
 * click to reveal a small (~26 item) always-relevant set. A forced-open
 * bundle gets a plain, non-interactive header (no caret, no click target
 * that would otherwise do nothing) rather than a toggle button stuck open. */
function BundleGroup({ label, definitions, selectedAssetId, onSelectAsset, forceOpen = false }: {
  label: string;
  definitions: ObjectDefinition[];
  selectedAssetId: string | null;
  onSelectAsset: (assetId: string) => void;
  forceOpen?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const isOpen = forceOpen || open;
  // Show up to 4 mini-thumb previews in the collapsed header so users can peek inside.
  const previewSlice = isOpen ? [] : definitions.slice(0, 4);

  return (
    <div className="editor-library-bundle">
      {forceOpen ? (
        <div className="editor-library-bundle-head">
          <span className="editor-library-bundle-label">{label}</span>
          <span className="editor-library-bundle-count">{definitions.length}</span>
        </div>
      ) : (
        <button type="button" className="editor-library-bundle-head" onClick={() => setOpen(!open)} aria-expanded={open}>
          <span className="editor-library-bundle-caret">{open ? '▾' : '▸'}</span>
          <span className="editor-library-bundle-label">{label}</span>
          {previewSlice.length > 0 && (
            <span className="editor-library-bundle-previews">
              {previewSlice.map((d) => (
                <ObjectThumbnail key={d.id} assetId={d.id} label={d.name} size={24} />
              ))}
            </span>
          )}
          <span className="editor-library-bundle-count">{definitions.length}</span>
        </button>
      )}
      {isOpen && (
        <TileGrid definitions={definitions} selectedAssetId={selectedAssetId} onSelectAsset={onSelectAsset} />
      )}
    </div>
  );
}

/** One author's full entry: name + total count, then each of their
 * bundles. Used both for the pinned Soulstack Originals entry
 * (`forceOpenBundles`) and for every real Shared library author, whose
 * bundles collapse by default. */
function AuthorBlock({ author, bundles, selectedAssetId, onSelectAsset, forceOpenBundles = false }: {
  author: string;
  bundles: Map<string, ObjectDefinition[]>;
  selectedAssetId: string | null;
  onSelectAsset: (assetId: string) => void;
  forceOpenBundles?: boolean;
}) {
  const authorTotal = [...bundles.values()].reduce((n, defs) => n + defs.length, 0);
  return (
    <div className="editor-library-author">
      <div className="editor-library-author-head">
        {author}
        <span className="editor-library-author-count">{authorTotal}</span>
      </div>
      {[...bundles.entries()].map(([bundle, definitions]) => (
        <BundleGroup
          key={bundle}
          label={bundle}
          definitions={definitions}
          selectedAssetId={selectedAssetId}
          onSelectAsset={onSelectAsset}
          forceOpen={forceOpenBundles}
        />
      ))}
    </div>
  );
}

export function ObjectBrowser({
  searchQuery,
  onSearchQueryChange,
  selectedAssetId,
  onSelectAsset,
  title = 'Object Library',
  subtitle = 'Shared registry assets',
  searchInputClassName,
  hideHeader = false,
  pinOriginals = false,
}: ObjectBrowserProps) {
  // Re-read the registry whenever it mutates (e.g. an uploaded GLB registers,
  // or the shared library manifest arrives).
  const registryVersion = useSyncExternalStore(subscribeObjectRegistry, getObjectRegistryVersion);
  const libraryLoadStatus = useSyncExternalStore(subscribeLibraryLoadStatus, getLibraryLoadStatus);
  // The <input> stays bound to the raw, un-debounced value so typing feels
  // instant; only the expensive ~5k-item scan below lags behind by
  // SEARCH_DEBOUNCE_MS.
  const debouncedQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);
  const searching = debouncedQuery.trim().length > 0;

  // Split out from the search-dependent memo below so typing doesn't re-run
  // these two full-array passes (~10k startsWith calls at 5k+ definitions)
  // on every keystroke - this only changes when the registry itself does.
  const { local, library } = useMemo(() => {
    void registryVersion;
    const all = listObjectDefinitions();
    return {
      local: all.filter((d) => !d.id.startsWith(LIBRARY_ID_PREFIX)),
      library: all.filter((d) => d.id.startsWith(LIBRARY_ID_PREFIX)),
    };
  }, [registryVersion]);

  const { groupedDefinitions, originals, originalsSearchHits, libraryAuthors, librarySearchHits, libraryCount } = useMemo(() => {
    // Skipped entirely (not just hidden) when pinOriginals - this same data
    // already renders elsewhere (BottomDock's Core pack/Uploads, and the
    // pinned Soulstack Originals entry below), so there is nothing for a
    // matching search to find here that filtering would be worth the work
    // for.
    const localMatches = pinOriginals ? [] : local.filter((d) => matchesSearch(d, debouncedQuery));
    const grouped = pinOriginals
      ? []
      : CATEGORY_ORDER
          .map((category) => ({
            category,
            label: CATEGORY_LABELS[category],
            definitions: localMatches.filter((d) => d.category === category),
          }))
          .filter((group) => group.definitions.length > 0);

    // The pinned entry's own matches - computed independently of `grouped`
    // above (which stays off entirely when pinOriginals is set). Not split
    // by category: unlike the real Shared library, this is a small (~26
    // item), always-relevant set with no need for that structure - it's
    // still split by each definition's own `editor.bundle` though, rather
    // than assuming everything is one hardcoded "Core Pack" bucket, so a
    // future Soulstack Originals sub-pack with its own bundle name renders
    // under its own heading instead of silently landing in the wrong one.
    const originalsMatches = pinOriginals
      ? local.filter((d) => d.editor.author === SOULSTACK_ORIGINALS_AUTHOR && matchesSearch(d, debouncedQuery))
      : [];
    const originalsBundles = new Map<string, ObjectDefinition[]>();
    for (const d of originalsMatches) {
      const bundleKey = authorBundleOf(d).bundle ?? 'Core Pack';
      const list = originalsBundles.get(bundleKey) ?? [];
      list.push(d);
      originalsBundles.set(bundleKey, list);
    }

    // Library facets: author -> bundle, from each definition's own
    // editor.author/editor.bundle (falling back to parsing the legacy
    // "Author · Bundle (licence)" description for any that lack them).
    const authors = new Map<string, Map<string, ObjectDefinition[]>>();
    const searchHits: ObjectDefinition[] = [];
    for (const d of library) {
      if (searching) {
        if (searchHits.length < SEARCH_CAP && matchesSearch(d, debouncedQuery)) searchHits.push(d);
        continue;
      }
      const { author, bundle } = authorBundleOf(d);
      const authorKey = author ?? 'Library';
      const bundleKey = bundle ?? 'Misc';
      const bundles = authors.get(authorKey) ?? new Map<string, ObjectDefinition[]>();
      const list = bundles.get(bundleKey) ?? [];
      list.push(d);
      bundles.set(bundleKey, list);
      authors.set(authorKey, bundles);
    }

    return {
      groupedDefinitions: grouped,
      // While searching, the pinned block itself doesn't render, but its
      // matches still need their own labelled section (not silently folded
      // into "Shared library" as a third-party hit) - kept as its own flat
      // list here rather than a bundle-grouped Map, matching the small,
      // no-need-for-that-structure reasoning above.
      originals: searching ? new Map<string, ObjectDefinition[]>() : originalsBundles,
      originalsSearchHits: searching ? originalsMatches : [],
      libraryAuthors: authors,
      librarySearchHits: searchHits,
      libraryCount: library.length,
    };
  }, [debouncedQuery, searching, local, library, pinOriginals]);

  const renderedCount = useThumbnailStore(
    (state) => Object.keys(state.thumbnails).length,
  );
  const thumbnailsReady = useThumbnailStore((state) => state.ready);

  return (
    <section className="editor-library">
      {!hideHeader && (
        <>
          <header className="editor-section-header">
            <div>
              <span className="editor-section-eyebrow">{title}</span>
              <h3>{subtitle}</h3>
            </div>
          </header>

          <label className="editor-search">
            <span>Search</span>
            <input
              className={searchInputClassName}
              value={searchQuery}
              onChange={(event) => onSearchQueryChange(event.target.value)}
              placeholder={libraryCount > 0 ? `desk, alien, kenney... (${libraryCount.toLocaleString()} in the library)` : 'desk, shelf, structural...'}
            />
          </label>
        </>
      )}

      {libraryLoadStatus === 'loading' && libraryCount === 0 && (
        <div className="editor-library-progress" role="status" aria-live="polite">
          Loading shared library…
        </div>
      )}

      {libraryLoadStatus === 'error' && (
        <div className="editor-library-error" role="alert">
          <span>Shared library unavailable{getLibraryLoadError() ? `: ${getLibraryLoadError()}` : '.'}</span>
          <button type="button" className="editor-library-error-retry" onClick={() => retryLibraryLoad()}>
            Retry
          </button>
        </div>
      )}

      {!thumbnailsReady ? (
        <div
          className="editor-library-progress"
          role="status"
          aria-live="polite"
        >
          Generating previews… {renderedCount} done
        </div>
      ) : null}

      {searching
        && groupedDefinitions.length === 0
        && originalsSearchHits.length === 0
        && librarySearchHits.length === 0 && (
        <div className="editor-empty-state">
          <p>No matches for "{searchQuery.trim()}".</p>
          <button type="button" className="editor-library-more" onClick={() => onSearchQueryChange('')}>
            Clear search
          </button>
        </div>
      )}

      <div className="editor-library-groups">
        {groupedDefinitions.map((group) => (
          <div key={group.category} className="editor-library-group">
            <div className="editor-library-label">{group.label}</div>
            <TileGrid definitions={group.definitions} selectedAssetId={selectedAssetId} onSelectAsset={onSelectAsset} />
          </div>
        ))}

        {!searching && originals.size > 0 && (
          <div className="editor-library-group">
            <AuthorBlock
              author={SOULSTACK_ORIGINALS_AUTHOR}
              bundles={originals}
              selectedAssetId={selectedAssetId}
              onSelectAsset={onSelectAsset}
              forceOpenBundles
            />
          </div>
        )}

        {searching && originalsSearchHits.length > 0 && (
          <div className="editor-library-group">
            <div className="editor-library-label">{SOULSTACK_ORIGINALS_AUTHOR}</div>
            <TileGrid definitions={originalsSearchHits} selectedAssetId={selectedAssetId} onSelectAsset={onSelectAsset} />
          </div>
        )}

        {searching && librarySearchHits.length > 0 && (
          <div className="editor-library-group">
            <div className="editor-library-label">Shared library</div>
            {librarySearchHits.length >= SEARCH_CAP && (
              <div className="editor-library-progress">First {SEARCH_CAP} matches; refine the search.</div>
            )}
            <TileGrid definitions={librarySearchHits} selectedAssetId={selectedAssetId} onSelectAsset={onSelectAsset} />
          </div>
        )}

        {!searching && libraryAuthors.size > 0 && (
          <div className="editor-library-group">
            <div className="editor-library-label">Shared library · CC0</div>
            {[...libraryAuthors.entries()].map(([author, bundles]) => (
              <AuthorBlock key={author} author={author} bundles={bundles} selectedAssetId={selectedAssetId} onSelectAsset={onSelectAsset} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}